import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { api, QUOTE_EVENT } from "@/lib/api";

const INTERESTS = [
    "General Enquiry",
    "Building Wires",
    "LT Power Cables",
    "Control Cables",
    "Instrumentation",
    "Flexible & Welding",
    "Solar PV",
    "Submersible",
    "Armoured",
    "HT Cables",
    "Glands & Lugs",
    "Cable Jointing Kits",
];

const inputCls =
    "w-full bg-paper border border-line rounded-md px-4 py-3 text-sm text-ink placeholder:text-dim focus:outline-none focus:border-copper transition-colors duration-300";

export default function Enquiry() {
    const [form, setForm] = useState({
        name: "",
        company: "",
        phone: "",
        email: "",
        interest: "General Enquiry",
        size: "",
        message: "",
    });
    const [sending, setSending] = useState(false);

    useEffect(() => {
        const handler = (e) => {
            const { interest, message } = e.detail || {};
            setForm((f) => ({
                ...f,
                interest: INTERESTS.includes(interest) ? interest : "General Enquiry",
                message: message || (interest ? `Quote request for: ${interest}` : f.message),
            }));
        };
        window.addEventListener(QUOTE_EVENT, handler);
        return () => window.removeEventListener(QUOTE_EVENT, handler);
    }, []);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await api.post("/enquiries", form);
            toast.success("Enquiry received — our desk responds within one working day.", {
                description: `Ref logged for ${form.name} · ${form.interest}`,
            });
            setForm({ name: "", company: "", phone: "", email: "", interest: "General Enquiry", size: "", message: "" });
        } catch {
            toast.error("Could not send the enquiry. Please retry or call our desk.");
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="enquiry" className="relative hud-bg py-20 lg:py-28 border-t border-line" data-testid="enquiry-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-14">
                <div>
                    <Reveal>
                        <Eyebrow className="mb-5">11 — Enquire</Eyebrow>
                        <h2 className="font-display font-bold tracking-tight leading-[1.05] text-3xl sm:text-4xl lg:text-5xl text-ink mb-6">
                            Tell us what <br />
                            needs to <em className="text-copper">flow.</em>
                        </h2>
                        <p className="text-mist leading-relaxed mb-10 max-w-md">
                            Share the BOQ, the odd size, or the jobsite emergency. We respond fast on working
                            days, and dispatch timelines are confirmed on every quotation.
                        </p>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div className="space-y-4 font-mono text-sm">
                            <p className="flex items-center gap-3 text-ink" data-testid="contact-phone">
                                <Phone size={14} className="text-copper" /> +91 900 81 77 176 · +91 080 41689333
                            </p>
                            <p className="flex items-center gap-3 text-ink" data-testid="contact-email">
                                <Mail size={14} className="text-copper" /> sales@roxycables.com
                            </p>
                            <p className="flex items-start gap-3 text-ink" data-testid="contact-address">
                                <MapPin size={14} className="text-copper mt-0.5" />
                                # 80/4, Bassettepet, A M Lane, B V K Iyengar Road cross,
                                <br />
                                behind milk booth, Chickpet, Bengaluru 560053
                            </p>
                        </div>
                    </Reveal>
                </div>

                <Reveal delay={0.15}>
                    <form
                        onSubmit={submit}
                        data-testid="enquiry-form"
                        className="range-card rounded-md border border-line bg-white p-6 md:p-8 space-y-4 transition-all duration-500"
                    >
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim block mb-1.5">Name *</label>
                                <input
                                    data-testid="enquiry-input-name"
                                    className={inputCls}
                                    required
                                    value={form.name}
                                    onChange={set("name")}
                                    placeholder="Your name"
                                />
                            </div>
                            <div>
                                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim block mb-1.5">Company</label>
                                <input
                                    data-testid="enquiry-input-company"
                                    className={inputCls}
                                    value={form.company}
                                    onChange={set("company")}
                                    placeholder="Company / firm"
                                />
                            </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim block mb-1.5">Phone *</label>
                                <input
                                    data-testid="enquiry-input-phone"
                                    className={inputCls}
                                    required
                                    type="tel"
                                    value={form.phone}
                                    onChange={set("phone")}
                                    placeholder="+91 ..."
                                />
                            </div>
                            <div>
                                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim block mb-1.5">Email</label>
                                <input
                                    data-testid="enquiry-input-email"
                                    className={inputCls}
                                    type="email"
                                    value={form.email}
                                    onChange={set("email")}
                                    placeholder="you@company.in"
                                />
                            </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim block mb-1.5">Interest</label>
                                <select
                                    data-testid="enquiry-select-interest"
                                    className={`${inputCls} appearance-none`}
                                    value={form.interest}
                                    onChange={set("interest")}
                                >
                                    {INTERESTS.map((i) => (
                                        <option key={i} value={i}>
                                            {i}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim block mb-1.5">Required Size</label>
                                <input
                                    data-testid="enquiry-input-size"
                                    className={inputCls}
                                    value={form.size}
                                    onChange={set("size")}
                                    placeholder="e.g. 4 core x 25 sqmm"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim block mb-1.5">Requirement</label>
                            <textarea
                                data-testid="enquiry-input-message"
                                className={`${inputCls} min-h-[110px] resize-y`}
                                value={form.message}
                                onChange={set("message")}
                                placeholder="Sizes, lengths, target delivery..."
                            />
                        </div>
                        <button
                            type="submit"
                            data-testid="enquiry-submit-button"
                            disabled={sending}
                            className="w-full rounded-full bg-copper text-white font-mono uppercase tracking-[0.2em] text-[11px] py-4 hover:bg-copper-bright transition-colors duration-300 disabled:opacity-50"
                        >
                            {sending ? "Sending..." : "Send Enquiry"}
                        </button>
                    </form>
                </Reveal>
            </div>
        </section>
    );
}
